import React, { useState } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Backdrop,
    CircularProgress,
    Card,
    CardContent,
    Chip,
    IconButton,
    Tooltip,
} from "@mui/material";
import { RefreshSharp, Visibility, Business } from "@mui/icons-material";
import axiosInstance from "../../api/axiosInstance";
import base_url from "../../constants";
import useNotification from "../common/Notification";
import { WB_SX, WB_UI } from "../../ui/ux/uiTokens";

const BusinessAccounts = () => {
    const [businessId, setBusinessId] = useState("");
    const [wabas, setWabas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedWaba, setSelectedWaba] = useState(null);
    const showNotification = useNotification();

    const fetchBusinessAccounts = async () => {
        // Business ID can be optional if stored in config, but let's allow override
        const businessIdToUse = businessId.trim() || null;
        
        if (!businessIdToUse) {
            showNotification("Please enter Business ID or configure it in Settings", "error");
            return;
        }

        setLoading(true);
        try {
            // Backend will automatically use config from database based on authenticated user
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/business/${businessIdToUse}/wabas`
            );

            if (response.data?.data) {
                setWabas(response.data.data);
                showNotification("Business accounts fetched successfully!", "success");
            } else {
                setWabas(response.data || []);
                showNotification("Business accounts fetched successfully!", "success");
            }
        } catch (error) {
            console.error("Error fetching business accounts:", error);
            showNotification(
                error.response?.data?.error || "Failed to fetch business accounts",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    const fetchWabaDetails = async (wabaId) => {
        setLoading(true);
        try {
            // Backend will automatically use config from database based on authenticated user
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/waba/${wabaId}`,
                {
                    params: {
                        save_to_db: true,
                    },
                }
            );

            setSelectedWaba(response.data);
            showNotification("WABA details fetched successfully!", "success");
        } catch (error) {
            console.error("Error fetching WABA details:", error);
            showNotification(
                error.response?.data?.error || "Failed to fetch WABA details",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={WB_SX.page}>
            <Typography
                variant="h6"
                sx={WB_SX.pageTitle}
            >
                WhatsApp Business Accounts
            </Typography>

            <Backdrop
                sx={(theme) => ({
                    color: "#fff",
                    zIndex: theme.zIndex.drawer + 1,
                })}
                open={loading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            <Card sx={{ mb: WB_UI.spacing.sectionGap, ...WB_SX.card }}>
                <CardContent sx={WB_SX.cardContent}>
                    <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                        <TextField
                            label="Business ID (Optional - override saved value)"
                            variant="outlined"
                            size="small"
                            value={businessId}
                            onChange={(e) => setBusinessId(e.target.value)}
                            placeholder="Enter Business ID to override saved value"
                            sx={{ flex: 1, minWidth: "200px" }}
                            helperText="Leave empty to use saved value from Settings"
                        />
                        <Button
                            variant="contained"
                            onClick={fetchBusinessAccounts}
                            startIcon={<Business />}
                            sx={{ minWidth: "150px", ...WB_SX.primaryButton }}
                        >
                            Fetch Accounts
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={fetchBusinessAccounts}
                            startIcon={<RefreshSharp />}
                            sx={WB_SX.secondaryButton}
                        >
                            Refresh
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {wabas.length > 0 && (
                <TableContainer component={Paper} elevation={0} sx={WB_SX.tableContainer}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: WB_UI.color.tableHeadBg }}>
                                <TableCell sx={WB_SX.tableHeadCell}>ID</TableCell>
                                <TableCell sx={WB_SX.tableHeadCell}>Name</TableCell>
                                <TableCell sx={WB_SX.tableHeadCell}>Status</TableCell>
                                <TableCell sx={WB_SX.tableHeadCell}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {wabas.map((waba, index) => (
                                <TableRow key={waba.id || index} sx={WB_SX.tableRow}>
                                    <TableCell>{waba.id}</TableCell>
                                    <TableCell>{waba.name || "N/A"}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={waba.account_status || "UNKNOWN"}
                                            color={
                                                waba.account_status === "ACTIVE"
                                                    ? "success"
                                                    : "default"
                                            }
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title="View Details">
                                            <IconButton
                                                size="small"
                                                onClick={() => fetchWabaDetails(waba.id)}
                                                color="primary"
                                            >
                                                <Visibility />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {selectedWaba && (
                <Card sx={{ mt: 3, ...WB_SX.card }}>
                    <CardContent sx={WB_SX.cardContent}>
                        <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: "-0.02em", mb: 2 }}>
                            WABA Details
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <Typography>
                                <strong>ID:</strong> {selectedWaba.id}
                            </Typography>
                            <Typography>
                                <strong>Name:</strong> {selectedWaba.name || "N/A"}
                            </Typography>
                            <Typography>
                                <strong>Status:</strong>{" "}
                                <Chip
                                    label={selectedWaba.account_status || "UNKNOWN"}
                                    color={
                                        selectedWaba.account_status === "ACTIVE"
                                            ? "success"
                                            : "default"
                                    }
                                    size="small"
                                />
                            </Typography>
                            {selectedWaba.currency && (
                                <Typography>
                                    <strong>Currency:</strong> {selectedWaba.currency}
                                </Typography>
                            )}
                            {selectedWaba.timezone_id && (
                                <Typography>
                                    <strong>Timezone:</strong> {selectedWaba.timezone_id}
                                </Typography>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default BusinessAccounts;

